import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApp } from "@/components/AppShell";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell, CheckCheck, AtSign, MessageSquare, UserPlus, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRealtime } from "@/lib/realtime";
import { cn } from "@/lib/utils";

const kindIcon = (kind) => {
  if (kind === "mention") return AtSign;
  if (kind === "comment") return MessageSquare;
  if (kind === "assignment") return UserPlus;
  return Circle;
};
const kindColor = (kind) => {
  if (kind === "mention") return "text-purple-500";
  if (kind === "comment") return "text-sky-500";
  if (kind === "assignment") return "text-emerald-500";
  return "text-muted-foreground";
};

export default function NotificationBell() {
  const { user } = useAuth();
  const { openIssue } = useApp();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const refresh = async () => {
    if (!user) return;
    const [list, c] = await Promise.all([
      api.get("/notifications", { params: { limit: 30 } }),
      api.get("/notifications/unread-count"),
    ]);
    setItems(list.data);
    setUnread(c.data.count);
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user?.id]);

  useRealtime((ev) => {
    if (ev.type === "notification.new") refresh();
  }, [user?.id]);

  useEffect(() => { if (open) refresh(); /* eslint-disable-next-line */ }, [open]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    refresh();
  };
  const markAll = async () => { await api.post("/notifications/read-all"); refresh(); };

  const handleClick = (n) => {
    setOpen(false);
    if (!n.read) markRead(n.id);
    if (n.issue_id) openIssue(n.issue_id);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative h-9 w-9 grid place-items-center rounded-md hover:bg-accent transition-colors"
          data-testid="notification-bell"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-mono font-semibold grid place-items-center" data-testid="notification-count">
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <div className="text-sm font-medium">Notifications</div>
          {unread > 0 ? (
            <Button variant="ghost" size="sm" onClick={markAll} className="h-7 text-xs gap-1" data-testid="mark-all-read">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </Button>
          ) : null}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Bell className="w-6 h-6 mx-auto mb-2 opacity-50" />
              You're all caught up.
            </div>
          ) : (
            items.map((n) => {
              const Icon = kindIcon(n.kind);
              const color = kindColor(n.kind);
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-${n.id}`}
                  className={cn(
                    "w-full text-left px-3 py-2.5 flex gap-3 hover:bg-accent transition-colors border-b border-border/60",
                    !n.read ? "bg-primary/[0.04]" : ""
                  )}
                >
                  <div className={cn("mt-0.5 shrink-0", color)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{n.title}</div>
                    {n.body ? <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div> : null}
                    <div className="text-[10px] text-muted-foreground mt-1 font-mono">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.read ? <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" /> : null}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
